import { useEffect, useState } from "react";
import { Cookie, X, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useCookieConsent, CookiePreferences } from "@/hooks/useCookieConsent";
import { pushConsentBannerImpression } from "@/hooks/useCookieConsent";

const CookieBanner = () => {
  const { showBanner, acceptAll, rejectAll, savePreferences, preferences, resetConsent, setShowBanner } = useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [tempPreferences, setTempPreferences] = useState<CookiePreferences>(preferences);

  // Réouverture de la bannière depuis le footer (CustomEvent global)
  useEffect(() => {
    const handler = () => {
      setTempPreferences(preferences);
      setShowSettings(true);
      pushConsentBannerImpression('settings_modal');
    };
    window.addEventListener("open-cookie-banner", handler);
    return () => window.removeEventListener("open-cookie-banner", handler);
  }, [preferences]);

  // Impression de la bannière principale (1 seule fois à l'affichage)
  useEffect(() => {
    if (showBanner) pushConsentBannerImpression('banner');
  }, [showBanner]);

  if (!showBanner && !showSettings) return null;

  const handleSavePreferences = () => {
    savePreferences(tempPreferences);
    setShowSettings(false);
  };

  return (
    <>
      {showBanner && (
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t border-border shadow-2xl animate-slide-in">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-poppins font-bold text-lg mb-2">
                  Améliorons votre expérience ensemble 🍪
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  En acceptant tous les cookies, vous nous aidez à améliorer nos services et à vous proposer une expérience optimale et personnalisée.{" "}
                  <a href="/confidentialite" className="text-primary hover:underline font-medium">
                    En savoir plus
                  </a>
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <Button
                onClick={() => rejectAll('banner')}
                size="lg"
                variant="outline"
                className="font-semibold flex-1 md:flex-none order-2 md:order-1"
              >
                Tout refuser
              </Button>
              <Button
                onClick={() => setShowSettings(true)}
                size="lg"
                variant="outline"
                className="flex items-center gap-2 font-semibold flex-1 md:flex-none order-3 md:order-2"
              >
                <Settings className="w-4 h-4" />
                Personnaliser
              </Button>
              <Button
                onClick={() => acceptAll('banner')}
                size="lg"
                className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white font-semibold shadow-lg hover:shadow-xl transition-all flex-1 md:flex-none order-1 md:order-3"
              >
                Tout accepter
              </Button>
            </div>
          </div>
        </div>
      </div>
      )}

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Cookie className="w-6 h-6 text-primary" />
              Préférences des cookies
            </DialogTitle>
            <DialogDescription>
              Gérez vos préférences en matière de cookies. Les cookies essentiels sont toujours actifs car ils sont nécessaires au fonctionnement du site.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Cookies essentiels */}
            <div className="flex items-start justify-between gap-4 p-4 bg-muted/50 rounded-lg">
              <div className="flex-1">
                <Label className="text-base font-semibold mb-2 block">
                  Cookies essentiels
                </Label>
                <p className="text-sm text-muted-foreground">
                  Ces cookies sont nécessaires au fonctionnement du site web et ne peuvent pas être désactivés. Ils sont généralement établis en réponse à des actions que vous effectuez (connexion, préférences de confidentialité, etc.).
                </p>
              </div>
              <Switch
                checked={true}
                disabled
                className="mt-1"
              />
            </div>

            {/* Cookies analytiques */}
            <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
              <div className="flex-1">
                <Label className="text-base font-semibold mb-2 block">
                  Cookies analytiques
                </Label>
                <p className="text-sm text-muted-foreground">
                  Ces cookies nous permettent de compter les visites et sources de trafic afin de mesurer et améliorer les performances de notre site. Ils nous aident à savoir quelles pages sont les plus et les moins populaires.
                </p>
              </div>
              <Switch
                checked={tempPreferences.analytics}
                onCheckedChange={(checked) =>
                  setTempPreferences({ ...tempPreferences, analytics: checked })
                }
                className="mt-1"
              />
            </div>

            {/* Cookies marketing */}
            <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
              <div className="flex-1">
                <Label className="text-base font-semibold mb-2 block">
                  Cookies marketing
                </Label>
                <p className="text-sm text-muted-foreground">
                  Ces cookies peuvent être déposés par nos partenaires publicitaires. Ils permettent de créer un profil de vos intérêts et de vous montrer des publicités pertinentes sur d'autres sites.
                </p>
              </div>
              <Switch
                checked={tempPreferences.marketing}
                onCheckedChange={(checked) =>
                  setTempPreferences({ ...tempPreferences, marketing: checked })
                }
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3 justify-end pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                rejectAll('settings_modal');
                setShowSettings(false);
              }}
            >
              Tout refuser
            </Button>
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Annuler
            </Button>
            <Button onClick={handleSavePreferences} className="bg-primary hover:bg-primary/90">
              Enregistrer mes choix
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieBanner;
